import { sickLeaveConfirmation } from "@/lib/bolovanja";
import { PrijelaznoRazdoblje } from "@/components/PrijelaznoRazdoblje";
import { AllocationEditor } from "@/components/workflow/AllocationEditor";
import { usePostavke } from "@/lib/postavke";
import { Select as DesignSelect } from "@/components/ui/design-system";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SkeletonTablica } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { useRed } from "@/context/RedContext";
import { dostupniDani, useDanas } from "@/lib/dan";
import { porukaDohvata } from "@/lib/greske";
import {
  osvjeziKontekst,
  procitajKontekst,
  radniciNaGradilistu,
  type RadnikNaGradilistu,
  type SnimljenKontekst,
} from "@/lib/kontekst";
import { osvjeziPoslano, procitajPoslano } from "@/lib/poslano";
import type { StavkaReda, ZapisZaSlanje } from "@/lib/red";
import {
  nacrtUZapis,
  NENAZOCNOSTI,
  type NacrtZapisa,
  type ZapisSati,
} from "@/lib/sati";
import { cn } from "@/lib/utils";
import { Link, useSearchParams } from "react-router-dom";
import {
  hasOfficeValues,
  HoursVisibility,
} from "@/components/workflow/HoursVisibility";
import { PeriodLockNotice, usePeriodLock } from "@/components/workflow/shared";
import { hasPermission, managerWindow, officeAccess } from "@/lib/workflow";

/**
 * Entering the day's hours — `docs/dizajn/ekran-unos-sati.dc.html`, ADR-0004
 * §2.5.
 *
 * Two taps per worker: the tick confirms the site's default shift. "Potvrdi
 * sve" is one tap for the whole crew. Typing 07:00–15:00 eighty-five times is
 * what makes a manager give up and write the hours from memory in the evening,
 * which is the thing this module exists to end.
 *
 * The record still carries a start and an end, because NN 55/24 čl. 13
 * requires them and a total on its own is the most serious breach an employer
 * can commit here. The default shift is how both are true at once.
 *
 * A tap on the **name** rather than the tick opens the exception: a different
 * interval, a break, field work, or an absence.
 */

function inicijali(ime: string): string {
  return ime
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((dio) => dio[0]?.toUpperCase() ?? "")
    .join("");
}

type StanjeRetka =
  | { vrsta: "otvoren" }
  | { vrsta: "ceka"; zapis: ZapisZaSlanje }
  /** The server refused it and said why. Needs a person, not a signal. */
  | { vrsta: "odbijen"; stavka: StavkaReda }
  | { vrsta: "poslan"; zapis: ZapisSati }
  | { vrsta: "zapreka"; razlog: string };

/**
 * The entry a correction is correcting, whichever side it currently sits on.
 *
 * The two shapes differ, but the correction only needs what they share: the
 * values to open the form on, and the `client_uuid` that makes it the same
 * entry rather than a second one.
 */
type Ispravljani = ZapisZaSlanje | ZapisSati;

/** Which sheet is open over the crew, if any. */
type Listic =
  | {
      vrsta: "iznimka";
      radnik: RadnikNaGradilistu;
      /** Set when an existing entry is being corrected, not a new one made. */
      postojeci?: Ispravljani;
      /**
       * Whether that entry is still in the queue on this phone.
       *
       * It decides how the correction is stored, not what it says: one in the
       * queue is replaced where it stands, one the server already has joins
       * the queue as a fresh item carrying the old key (O-20).
       */
      uRedu?: boolean;
    }
  | { vrsta: "odbijen"; radnik: RadnikNaGradilistu; stavka: StavkaReda };

/**
 * The day in three numbers — frame 4 of the design.
 *
 * An exception is any entry that is not simply the site's shift confirmed: a
 * different interval, a break, or field work. It is counted separately from a
 * plain confirmation because it is the number that tells a manager whether he
 * still remembers what he corrected.
 */
function jeIznimka(
  zapis: ZapisZaSlanje | ZapisSati,
  pocetak: string,
  kraj: string,
): boolean {
  return (
    (zapis.pocetak ?? "").slice(0, 5) !== pocetak ||
    (zapis.kraj ?? "").slice(0, 5) !== kraj ||
    zapis.zastoji_min > 0 ||
    zapis.teren
  );
}

export function UnosSatiPage() {
  const { prijelazno } = usePostavke();
  const [rosterParams] = useSearchParams();
  const { user } = useAuth();
  const { stavke, dodaj, ispraviStavku, ukloniStavku } = useRed();

  const danasIso = useDanas();
  const manager = managerWindow(user);
  const dani = useMemo(
    () => dostupniDani(danasIso, manager ? 2 : 7),
    [danasIso, manager],
  );

  /**
   * Which day is being entered — `null` meaning "today", whatever today turns
   * out to be (Z-10).
   *
   * Held this way rather than as a date, so that midnight moves the screen on
   * its own. Storing the date and correcting it afterwards is what produced
   * the bug: the screen kept the day it was opened on and went on calling it
   * "Danas", so a manager who picked the phone up in the morning was entering
   * into yesterday.
   *
   * A day picked deliberately stays picked — it is the day he is working on,
   * and moving him off it would be the same failure in the other direction.
   */
  const [odabraniDan, setOdabraniDan] = useState<string | null>(() => rosterParams.get("date"));
  const datum = useMemo(() => {
    if (odabraniDan === null) return danasIso;
    // A chosen day can fall out of the seven-day window while the screen is
    // open. It cannot be entered any more (NN 55/24 čl. 15), and leaving it
    // selected would leave the select showing a value it does not contain.
    return dani.some((dan) => dan.iso === odabraniDan)
      ? odabraniDan
      : (dani[dani.length - 1]?.iso ?? danasIso);
  }, [odabraniDan, dani, danasIso]);

  const [kontekst, setKontekst] = useState<SnimljenKontekst | null>(null);
  const [gradilisteId, setGradilisteId] = useState<string | null>(() => rosterParams.get("gradiliste_id"));
  // Carries the site and day it was read for, so a day that is being switched
  // can never be shown under another day's heading while its own read is still
  // in flight.
  const [poslani, setPoslani] = useState<{
    gradilisteId: string;
    datum: string;
    zapisi: ZapisSati[];
  } | null>(null);
  const [greska, setGreska] = useState<string | null>(null);
  const [ucitava, setUcitava] = useState(true);
  const [listic, setListic] = useState<Listic | null>(null);
  const [ready, setReady] = useState("");

  // The stored copy first, the network second. A manager who opens this in a
  // basement gets his crew from the phone instead of a spinner that never
  // resolves; when the network does answer, the copy is replaced.
  useEffect(() => {
    const kontroler = new AbortController();

    const ucitaj = async () => {
      // Cleared for the newly chosen day: an error about yesterday must not
      // stay on screen while today is being loaded.
      setGreska(null);
      const spremljeno = await procitajKontekst(datum).catch(() => null);
      if (spremljeno && !kontroler.signal.aborted) {
        setKontekst(spremljeno);
        setUcitava(false);
      }

      try {
        const svjeze = await osvjeziKontekst(datum, kontroler.signal);
        if (!kontroler.signal.aborted) {
          setKontekst(svjeze);
          setGreska(null);
        }
      } catch (error: unknown) {
        if (kontroler.signal.aborted) return;
        // Only worth saying when there is nothing to show. With a stored copy
        // on screen, a failed refresh is what offline looks like, not a fault.
        if (!spremljeno) {
          setKontekst(null);
          setGreska(porukaDohvata(error));
        }
      } finally {
        if (!kontroler.signal.aborted) setUcitava(false);
      }
    };

    void ucitaj();
    return () => kontroler.abort();
  }, [datum]);

  const gradilista = useMemo(
    () => kontekst?.gradilista.filter((g) => g.aktivan) ?? [],
    [kontekst],
  );

  const odabrano = useMemo(
    () =>
      gradilista.find((g) => g.id === gradilisteId) ?? gradilista[0] ?? null,
    [gradilista, gradilisteId],
  );
  const lock = usePeriodLock(odabrano?.id ?? "", datum);
  const readingKey = `${odabrano?.id ?? ""}:${datum}`;

  // What the server has already accepted for this day: the stored answer
  // first, the network second (O-19).
  //
  // The failed refresh deliberately changes nothing. Emptying the list here is
  // what made the screen offer hours that were already recorded as soon as the
  // signal went, and the entries that came back from that are still stuck on a
  // phone. Offline the manager sees the last answer this device got; it can be
  // out of date, but it never claims less than it knows.
  useEffect(() => {
    if (!odabrano) return;
    const kontroler = new AbortController();
    const gradilisteId = odabrano.id;

    const ucitaj = async () => {
      const spremljeno = await procitajPoslano(gradilisteId, datum).catch(
        () => null,
      );
      if (spremljeno && !kontroler.signal.aborted) {
        setPoslani({ gradilisteId, datum, zapisi: spremljeno });
      }

      try {
        const svjeze = await osvjeziPoslano(
          gradilisteId,
          datum,
          kontroler.signal,
        );
        if (!kontroler.signal.aborted) {
          setPoslani({ gradilisteId, datum, zapisi: svjeze });
        }
      } catch {
        // Offline, or the request was aborted by a change of day. Either way
        // the stored copy stands.
      } finally {
        if (!kontroler.signal.aborted) setReady(`${gradilisteId}:${datum}`);
      }
    };

    void ucitaj();
    return () => kontroler.abort();
  }, [odabrano, datum, stavke]);

  const posada = useMemo(
    () =>
      kontekst && odabrano ? radniciNaGradilistu(kontekst, odabrano.id) : [],
    [kontekst, odabrano],
  );

  /** The stored answer, but only if it is this site's and this day's. */
  const zapisiPoslani = useMemo(
    () =>
      poslani &&
      poslani.gradilisteId === odabrano?.id &&
      poslani.datum === datum
        ? poslani.zapisi
        : [],
    [poslani, odabrano, datum],
  );

  // Order matters and is the second half of O-19: the queue wins over the
  // server's answer for the same worker and day. An entry made at 17:30 is on
  // the phone and not yet anywhere else, and a row that kept saying
  // "Potvrđeno" from the cached answer would hide the correction that was just
  // made.
  const stanjeRetka = useCallback(
    (radnik: RadnikNaGradilistu): StanjeRetka => {
      if (radnik.zapreka && !prijelazno) {
        return { vrsta: "zapreka", razlog: radnik.zapreka };
      }
      const uRedu = stavke.find(
        (stavka) =>
          stavka.zapis.unio_korisnik_id === user?.id &&
          stavka.zapis.radnik_id === radnik.radnikId &&
          stavka.zapis.datum === datum &&
          stavka.zapis.gradiliste_id === odabrano?.id,
      );
      if (uRedu) {
        return uRedu.stanje === "odbijen"
          ? { vrsta: "odbijen", stavka: uRedu }
          : { vrsta: "ceka", zapis: uRedu.zapis };
      }
      const zapis = zapisiPoslani.find((z) => z.radnik_id === radnik.radnikId);
      return zapis ? { vrsta: "poslan", zapis } : { vrsta: "otvoren" };
    },
    [stavke, zapisiPoslani, datum, odabrano, user?.id, prijelazno],
  );

  const potvrdi = useCallback(
    async (
      radnik: RadnikNaGradilistu,
      izmjene?: Partial<NacrtZapisa>,
      /**
       * The entry being corrected, when there is one. Its `client_uuid` is
       * carried over: a correction is the same entry changed, and a new key
       * would let the server store it a second time.
       */
      postojeci?: Ispravljani,
      /** Whether `postojeci` is still in this phone's queue. */
      uRedu = false,
    ) => {
      if (
        !odabrano ||
        lock.blocked ||
        (!officeAccess(user) && zapisiPoslani.some(hasOfficeValues))
      )
        return;
      const nacrt: NacrtZapisa = {
        radnikId: radnik.radnikId,
        gradilisteId: odabrano.id,
        datum,
        pocetak: odabrano.smjena_pocetak.slice(0, 5),
        kraj: odabrano.smjena_kraj.slice(0, 5),
        zastojiMin: 0,
        teren: false,
        nenazocnost: null,
        ...izmjene,
      };
      const zapis = nacrtUZapis(nacrt, user?.id ?? null);

      // The key is the correction. Without it the server has no way to know
      // this is the same entry changed, stores it a second time, and refuses
      // it against the first one under the twelve-hour rule — which is how a
      // manager fixing one shift ends up with a refusal he cannot clear
      // (Z-12, O-20).
      if (postojeci) {
        const ispravak = { ...zapis, client_uuid: postojeci.client_uuid };
        // Already queued: replaced where it stands, so the queue keeps one
        // item per entry. Already sent: it joins the queue as a new item, and
        // the server recognises the key and updates what it holds.
        await (uRedu ? ispraviStavku(ispravak) : dodaj(ispravak));
        return;
      }
      await dodaj(zapis);
    },
    [odabrano, datum, dodaj, ispraviStavku, user, lock.blocked, zapisiPoslani],
  );

  const potvrdiSve = useCallback(async () => {
    for (const radnik of posada) {
      if (stanjeRetka(radnik).vrsta === "otvoren") {
        await potvrdi(radnik);
      }
    }
  }, [posada, stanjeRetka, potvrdi]);

  if (ucitava && !kontekst) {
    return <SkeletonTablica redaka={4} />;
  }

  if (greska) {
    return (
      <EmptyState naslov="Raspored se ne može učitati" objasnjenje={greska} />
    );
  }

  if (!odabrano) {
    return (
      <EmptyState
        naslov="Nemate gradilište"
        objasnjenje="Kad vam kadrovska dodijeli gradilište, ovdje ćete unositi sate."
      />
    );
  }

  const pocetakSmjene = odabrano.smjena_pocetak.slice(0, 5);
  const krajSmjene = odabrano.smjena_kraj.slice(0, 5);
  const stanja = posada.map(stanjeRetka);
  const zapisi = stanja.flatMap((stanje) =>
    stanje.vrsta === "ceka" || stanje.vrsta === "poslan" ? [stanje.zapis] : [],
  );

  const otvoreno = stanja.filter((stanje) => stanje.vrsta === "otvoren").length;
  const nenazocni = zapisi.filter(
    (zapis) => zapis.tip_zapisa === "nenazocnost",
  ).length;
  const iznimke = zapisi.filter(
    (zapis) =>
      zapis.tip_zapisa === "rad" && jeIznimka(zapis, pocetakSmjene, krajSmjene),
  ).length;
  const potvrdeno = zapisi.length - nenazocni - iznimke;

  return (
    <div className="flex flex-col gap-5">
      <PrijelaznoRazdoblje />
      {rosterParams.get("date") && rosterParams.get("date") !== datum && <p role="status" className="rounded-lg border p-3">Dan iz gradilišnog popisa nije dostupan u dopuštenom razdoblju unosa. Prikazan je dan {datum}.</p>}

      {hasPermission(user, "dan_rasporeda", "read") && (
        <Link
          className="inline-flex min-h-[52px] items-center font-semibold underline"
          to="/dani-rasporeda"
        >
          Brigade, taktovi i potvrda dana
        </Link>
      )}
      <header className="flex flex-col gap-1">
        {gradilista.length > 1 ? (
          <DesignSelect
            aria-label="Gradilište"
            className="h-[52px] rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] px-3 text-[var(--text-section)] font-semibold"
            value={odabrano.id}
            onChange={(dogadaj) => setGradilisteId(dogadaj.target.value)}
          >
            {gradilista.map((gradiliste) => (
              <option key={gradiliste.id} value={gradiliste.id}>
                {gradiliste.naziv}
              </option>
            ))}
          </DesignSelect>
        ) : (
          <h1 className="text-[var(--text-section)] leading-tight font-semibold">
            {odabrano.naziv}
          </h1>
        )}
        {/* Foremen retain the entry window. Managers and deputies may only
            review today and yesterday in the business timezone. */}
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="dan" className="oznaka">
            Dan
          </label>
          <DesignSelect
            id="dan"
            className="h-11 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] px-3"
            value={datum}
            onChange={(dogadaj) =>
              setOdabraniDan(
                dogadaj.target.value === danasIso ? null : dogadaj.target.value,
              )
            }
          >
            {dani.map((dan) => (
              <option key={dan.iso} value={dan.iso}>
                {dan.naziv}
              </option>
            ))}
          </DesignSelect>
          <span className="text-[var(--color-muted-foreground)]">
            smjena {odabrano.smjena_pocetak.slice(0, 5)}–
            {odabrano.smjena_kraj.slice(0, 5)}
          </span>
        </div>
      </header>

      <PeriodLockNotice state={lock.state} />
      <HoursVisibility
        records={[
          ...zapisiPoslani,
          ...(listic?.vrsta === "iznimka" && listic.postojeci
            ? [listic.postojeci]
            : []),
        ]}
      >
        <div
          data-testid="hours-readiness"
          data-day={datum}
          data-ready={ready === readingKey ? "true" : "false"}
        >
          {ready !== readingKey && (
            <p role="status">Učitavanje evidencije dana…</p>
          )}
          <fieldset
            disabled={lock.blocked || ready !== readingKey}
            className="min-w-0 space-y-5"
          >
            {posada.length === 0 ? (
              <EmptyState
                naslov="Nitko nije na rasporedu"
                objasnjenje="Kad kadrovska rasporedi radnike na ovo gradilište, pojavit će se ovdje."
              />
            ) : (
              <>
                {/* Frame 4 of the design: the day in the three numbers a manager
              checks before he goes home. */}
                <p className="text-[var(--color-muted-foreground)]">
                  <span className="broj">{potvrdeno}</span> potvrđeno ·{" "}
                  <span className="broj">{iznimke}</span>{" "}
                  {iznimke === 1 ? "iznimka" : "iznimke"} ·{" "}
                  <span className="broj">{nenazocni}</span> nenazočno ·{" "}
                  <span className="broj">{otvoreno}</span> preostalo
                </p>

                {otvoreno > 0 ? (
                  <Button
                    className="h-[52px] w-full"
                    onClick={() => void potvrdiSve()}
                  >
                    Potvrdi sve ({otvoreno})
                  </Button>
                ) : null}

                <ul className="flex flex-col gap-2">
                  {posada.map((radnik, redni) => (
                    <Redak
                      key={radnik.radnikId}
                      radnik={radnik}
                      smjena={`${pocetakSmjene}–${krajSmjene}`}
                      stanje={stanja[redni] ?? { vrsta: "otvoren" }}
                      naPotvrdu={() => void potvrdi(radnik)}
                      naOtvaranje={() => {
                        const stanje = stanja[redni];
                        // Opening a row that already has an entry is a correction of
                        // that entry, not a second one for the same worker and day.
                        // Only an untouched row starts something new.
                        if (stanje?.vrsta === "odbijen") {
                          setListic({
                            vrsta: "odbijen",
                            radnik,
                            stavka: stanje.stavka,
                          });
                          return;
                        }
                        setListic({
                          vrsta: "iznimka",
                          radnik,
                          postojeci:
                            stanje?.vrsta === "ceka" ||
                            stanje?.vrsta === "poslan"
                              ? stanje.zapis
                              : undefined,
                          uRedu: stanje?.vrsta === "ceka",
                        });
                      }}
                    />
                  ))}
                </ul>
              </>
            )}

            {hasPermission(user, "sati", "read") && <AllocationEditor siteId={odabrano.id} date={datum} refreshKey={JSON.stringify(zapisiPoslani)} pending={stavke.some((item) => item.zapis.gradiliste_id === odabrano.id && item.zapis.datum === datum)} blocked={lock.blocked || ready !== readingKey} />}

            {listic?.vrsta === "odbijen" ? (
              <ListicOdbijenog
                radnik={listic.radnik}
                stavka={listic.stavka}
                naOdustanak={() => setListic(null)}
                naPotvrduBolovanja={async () => {
                  await ispraviStavku({ ...listic.stavka.zapis, confirm_sick_leave: true });
                  setListic(null);
                }}
                naIspravak={() =>
                  setListic({
                    vrsta: "iznimka",
                    radnik: listic.radnik,
                    postojeci: listic.stavka.zapis,
                    // A refused entry is still in the queue: correcting it replaces
                    // it there rather than adding a second one beside it.
                    uRedu: true,
                  })
                }
                naUklanjanje={async () => {
                  await ukloniStavku(listic.stavka.zapis.client_uuid);
                  setListic(null);
                }}
              />
            ) : null}

            {listic?.vrsta === "iznimka" ? (
              <ListicIznimke
                radnik={listic.radnik}
                pocetak={odabrano.smjena_pocetak.slice(0, 5)}
                kraj={odabrano.smjena_kraj.slice(0, 5)}
                postojeci={listic.postojeci}
                naOdustanak={() => setListic(null)}
                naSpremanje={async (izmjene) => {
                  await potvrdi(
                    listic.radnik,
                    izmjene,
                    listic.postojeci,
                    listic.uRedu ?? false,
                  );
                  setListic(null);
                }}
              />
            ) : null}
          </fieldset>
        </div>
      </HoursVisibility>
    </div>
  );
}

function Redak({
  radnik,
  smjena,
  stanje,
  naPotvrdu,
  naOtvaranje,
}: {
  radnik: RadnikNaGradilistu;
  smjena: string;
  stanje: StanjeRetka;
  naPotvrdu: () => void;
  naOtvaranje: () => void;
}) {
  const { prijelazno } = usePostavke();
  const zakljucan = stanje.vrsta === "zapreka";

  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-3",
        zakljucan && "bg-[var(--color-status-bad-bg)]",
      )}
    >
      <span
        aria-hidden="true"
        className="broj flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--color-muted)] font-semibold"
      >
        {inicijali(radnik.ime)}
      </span>

      {/* The name is the control that opens the exception (design, Frame 2).
          A whole row that is one big button makes the tick unreachable with a
          glove on. */}
      <button
        type="button"
        disabled={zakljucan}
        onClick={naOtvaranje}
        className="min-w-0 flex-1 text-left disabled:cursor-default"
      >
        <span className="block font-medium">{radnik.ime}</span>
        <span className="block text-[var(--color-muted-foreground)]">
          {stanje.vrsta === "zapreka"
            ? stanje.razlog
            : // The reason is not repeated here: it is a sentence, and the row
              // is 44px tall. The badge says which state this is and the sheet
              // says why.
              [radnik.radnoMjesto, smjena].filter(Boolean).join(" · ")}
        </span>
        {prijelazno && radnik.zapreka && <span className="block text-sm text-red-800">Ne bi smio raditi · prijelazno: {radnik.zapreka}</span>}
      </button>

      {stanje.vrsta === "otvoren" ? (
        // 52px, because this is pressed outdoors with one hand.
        <Button className="h-[52px] px-5" onClick={naPotvrdu}>
          Potvrdi
        </Button>
      ) : stanje.vrsta === "odbijen" ? (
        // Not "Čeka slanje", which is what this used to say. Nothing is
        // waiting: the server answered, and what it needs is a person.
        <Badge ton="bad" className="shrink-0">
          Nije prihvaćeno
        </Badge>
      ) : stanje.vrsta === "ceka" ? (
        <span className="shrink-0 text-[var(--color-status-warn-fg)]">
          Čeka slanje
        </span>
      ) : stanje.vrsta === "poslan" ? (
        <span className="shrink-0 text-[var(--color-status-ok-fg)]">
          ✓ Potvrđeno
        </span>
      ) : (
        <span className="shrink-0 text-[var(--color-status-bad-fg)]">
          Ne smije raditi
        </span>
      )}
    </li>
  );
}

/**
 * What the server said about an entry it would not take, and the two things
 * that can be done about it.
 *
 * The reason has been stored on the device since the refusal; until now
 * nothing showed it. A manager saw a count in the banner, a row that claimed
 * "Čeka slanje", and no way to act — so the queue could not empty and the only
 * remaining move was to enter the hours again, which is what produced the
 * refusals in the first place.
 *
 * Two actions and no third: the entry is either wrong and gets fixed, or it
 * does not belong and goes. "Try again" is deliberately absent — the same
 * entry sent again gets the same answer.
 */
function ListicOdbijenog({
  radnik,
  stavka,
  naOdustanak,
  naIspravak,
  naUklanjanje,
  naPotvrduBolovanja,
}: {
  radnik: RadnikNaGradilistu;
  stavka: StavkaReda;
  naPotvrduBolovanja: () => Promise<void>;
  naOdustanak: () => void;
  naIspravak: () => void;
  naUklanjanje: () => Promise<void>;
}) {
  const [potvrda, setPotvrda] = useState(false);
  const [uklanja, setUklanja] = useState(false);
  const [conflictBusy, setConflictBusy] = useState(false);
  const [conflictError, setConflictError] = useState("");

  if (potvrda) {
    return (
      <DijalogUklanjanja
        radnik={radnik}
        uklanja={uklanja}
        naOdustanak={() => setPotvrda(false)}
        naPotvrdu={() => {
          setUklanja(true);
          void naUklanjanje().finally(() => setUklanja(false));
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-20 flex items-end bg-black/40">
      <div
        role="dialog"
        aria-label={`Nije prihvaćeno: ${radnik.ime}`}
        className="max-h-[90vh] w-full overflow-y-auto rounded-t-[var(--radius-card)] bg-[var(--color-card)] p-5"
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-[var(--text-section)] font-semibold">
            {radnik.ime}
          </h2>
          <Button variant="outline" onClick={naOdustanak}>
            Zatvori
          </Button>
        </div>

        <div className="flex flex-col gap-4">
          <Badge ton="bad" className="self-start">
            Nije prihvaćeno
          </Badge>

          {/* The server's own words. Not paraphrased: it is the only place the
              rule that refused this is stated, and a rewritten version would
              drift from what the server actually checks. */}
          <p className="text-[var(--color-status-bad-fg)]">
            {stavka.razlog ?? "Poslužitelj nije naveo razlog."}
          </p>

          <p className="text-[var(--color-muted-foreground)]">
            Unos je na telefonu i nije evidentiran. Ispravite ga i poslat će se
            sam, ili ga uklonite ako ne treba.
          </p>

          {stavka.razlog === sickLeaveConfirmation && <>
            {conflictError && <p role="alert">{conflictError}</p>}
            <Button className="h-[52px] w-full" disabled={conflictBusy} onClick={() => {
              setConflictBusy(true);
              void naPotvrduBolovanja().catch((cause: unknown) => setConflictError(porukaDohvata(cause))).finally(() => setConflictBusy(false));
            }}>Ipak upiši sate</Button>
          </>}
          <Button className="h-[52px] w-full" onClick={naIspravak}>
            Ispravi
          </Button>
          <Button
            variant="outline"
            className="h-[52px] w-full"
            onClick={() => setPotvrda(true)}
          >
            Ukloni
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The confirmation before an entry is dropped.
 *
 * From the design system: the title is the question, and the button is the
 * action it names. Never "U redu" — on a small screen, under sunlight, "U
 * redu" next to a question is answered by muscle memory and the person is not
 * told what they just agreed to.
 */
function DijalogUklanjanja({
  radnik,
  uklanja,
  naOdustanak,
  naPotvrdu,
}: {
  radnik: RadnikNaGradilistu;
  uklanja: boolean;
  naOdustanak: () => void;
  naPotvrdu: () => void;
}) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div
        role="alertdialog"
        aria-label={`Ukloniti unos za ${radnik.ime}?`}
        className="w-full max-w-md rounded-[var(--radius-card)] bg-[var(--color-card)] p-5"
      >
        <h2 className="text-[var(--text-section)] font-semibold">
          Ukloniti unos za {radnik.ime}?
        </h2>
        <p className="mt-2 text-[var(--color-muted-foreground)]">
          Unos se briše s telefona i neće biti evidentiran. Sati koji su već
          zaprimljeni ostaju kakvi jesu.
        </p>

        <div className="mt-5 flex flex-col gap-2">
          <Button
            className="h-[52px] w-full"
            disabled={uklanja}
            onClick={naPotvrdu}
          >
            Ukloni unos
          </Button>
          <Button
            variant="outline"
            className="h-[52px] w-full"
            disabled={uklanja}
            onClick={naOdustanak}
          >
            Odustani
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The exception — a shift that was not the usual one, or a day that was not
 * worked at all.
 *
 * Turning on the absence hides the times rather than greying them out: an
 * absence has no start and no end, and a disabled 07:00 still reads as eight
 * hours to somebody scanning the screen.
 */
function ListicIznimke({
  radnik,
  pocetak,
  kraj,
  postojeci,
  naOdustanak,
  naSpremanje,
}: {
  radnik: RadnikNaGradilistu;
  pocetak: string;
  kraj: string;
  /** An entry that already exists, whose values this opens on. */
  postojeci?: Ispravljani;
  naOdustanak: () => void;
  naSpremanje: (izmjene: Partial<NacrtZapisa>) => Promise<void>;
}) {
  // Correcting opens on what was entered, not on the site's shift. A form that
  // resets to the default makes the manager retype what was already right and
  // is how the wrong half gets corrected.
  const [od, setOd] = useState(postojeci?.pocetak?.slice(0, 5) ?? pocetak);
  const [do_, setDo] = useState(postojeci?.kraj?.slice(0, 5) ?? kraj);
  const [zastoji, setZastoji] = useState(String(postojeci?.zastoji_min ?? 0));
  const [teren, setTeren] = useState(postojeci?.teren ?? false);
  const [nenazocnost, setNenazocnost] = useState<string | null>(
    postojeci?.vrsta_nenazocnosti ?? null,
  );
  const [sprema, setSprema] = useState(false);

  return (
    <div className="fixed inset-0 z-20 flex items-end bg-black/40">
      <div
        role="dialog"
        aria-label={`Unos za ${radnik.ime}`}
        className="max-h-[90vh] w-full overflow-y-auto rounded-t-[var(--radius-card)] bg-[var(--color-card)] p-5"
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-[var(--text-section)] font-semibold">
            {radnik.ime}
          </h2>
          <Button variant="outline" onClick={naOdustanak}>
            Odustani
          </Button>
        </div>

        <div className="flex flex-col gap-4">
          {nenazocnost === null ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="pocetak">Početak</Label>
                  <Input
                    id="pocetak"
                    type="time"
                    value={od}
                    onChange={(dogadaj) => setOd(dogadaj.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="kraj">Kraj</Label>
                  <Input
                    id="kraj"
                    type="time"
                    value={do_}
                    onChange={(dogadaj) => setDo(dogadaj.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor="zastoji">Zastoji (min)</Label>
                <Input
                  id="zastoji"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={720}
                  value={zastoji}
                  onChange={(dogadaj) => setZastoji(dogadaj.target.value)}
                />
              </div>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  className="h-6 w-6"
                  checked={teren}
                  onChange={(dogadaj) => setTeren(dogadaj.target.checked)}
                />
                <span>Teren</span>
              </label>
            </>
          ) : (
            <div className="flex flex-col gap-1">
              <Label htmlFor="vrsta">Vrsta nenazočnosti</Label>
              <DesignSelect
                id="vrsta"
                className="h-11 rounded-[var(--radius-card)] border border-[var(--color-border)] px-3"
                value={nenazocnost}
                onChange={(dogadaj) => setNenazocnost(dogadaj.target.value)}
              >
                {NENAZOCNOSTI.map((vrsta) => (
                  <option key={vrsta.vrijednost} value={vrsta.vrijednost}>
                    {vrsta.naziv}
                  </option>
                ))}
              </DesignSelect>
            </div>
          )}

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              className="h-6 w-6"
              checked={nenazocnost !== null}
              onChange={(dogadaj) =>
                setNenazocnost(dogadaj.target.checked ? "godisnji" : null)
              }
            />
            <span>Nije radio (nenazočnost)</span>
          </label>

          <Button
            className="h-[52px] w-full"
            disabled={sprema}
            onClick={() => {
              setSprema(true);
              void naSpremanje({
                pocetak: od,
                kraj: do_,
                zastojiMin: Number(zastoji) || 0,
                teren,
                nenazocnost,
              }).finally(() => setSprema(false));
            }}
          >
            {postojeci
              ? "Spremi ispravak"
              : `Spremi za ${radnik.ime.split(" ")[0]}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
